import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { CFDI_USAGE_OPTIONS, FISCAL_REGIME_OPTIONS, getCfdiUsageOption, getFiscalRegimeOption } from '../constants/cfdiOptions';
import { updateCustomer } from '../utils/customerService';

const SaleDetails = ({ sale, customer, onSaleDetailsConfirmed, onError, onGoBack }) => {
    // Log raw props for debugging
    console.log("SaleDetails raw props:", { sale: JSON.stringify(sale), customer: JSON.stringify(customer) });
    
    // Log raw props for debugging
    console.log("SaleDetails raw props - SALE:", sale);
    console.log("SaleDetails raw props - CUSTOMER:", customer);
    
    // Safely handle the case where sale might be undefined or missing properties
    const safeSale = {
        id: sale?.id || 0,
        sucursalId: sale?.sucursalId || 0,
        sucursalName: sale?.sucursalName || '',
        fechaVenta: sale?.fechaVenta || new Date().toISOString(),
        formaPago: sale?.formaPago || '99', // Preserve formaPago from the original sale data
        total: sale?.total || 0,
        productos: Array.isArray(sale?.productos) ? sale.productos : []
    };

    // Safe default customer data
    const safeCustomer = {
        id: customer?.id || 0,
        rfc: customer?.rfc || '',
        razonSocial: customer?.razonSocial || '',
        email: customer?.email || '',
        telefono: customer?.telefono || '',
        codigoPostal: customer?.codigoPostal || '',
        usoCfdi: customer?.usoCfdi || 'G03', // Default to "Gastos en general" as most common
        regimenFiscal: customer?.regimenFiscal || '616' // Default to "Sin obligaciones fiscales"
    };

    const [isEditing, setIsEditing] = useState(false);
    const [editedCustomer, setEditedCustomer] = useState({ ...safeCustomer });
    const [originalRFC, setOriginalRFC] = useState(safeCustomer.rfc); // Track original RFC for updates
    
    // Log customer ID specifically for debugging the customer flow
    console.log(`SaleDetails - Customer ID: ${safeCustomer.id}`);
    const [isSubmitting, setIsSubmitting] = useState(false);
    
    // Log what we're using for debugging
    console.log("SaleDetails processed data:", { 
        safeSale: JSON.stringify(safeSale),
        safeCustomer: JSON.stringify(safeCustomer)
    });

    // Log product IDs specifically for debugging
    console.log("SaleDetails - Product IDs verification:");
    (safeSale.productos || []).forEach((product, index) => {
        console.log(`Product ${index}: ID=${product.id}, Name=${product.nombre}`);
    });

    const formatCurrency = (amount) => {
        return new Intl.NumberFormat('es-MX', {
            style: 'currency',
            currency: 'MXN'
        }).format(amount);
    };

    const formatDate = (dateString) => {
        try {
            console.log("Formatting date string:", dateString);
            
            if (!dateString) {
                console.warn("Empty date string provided");
                return "Fecha no disponible";
            }
            
            // Enhanced date parsing to handle multiple formats
            let date;
            
            if (typeof dateString === 'string') {
                // Check for MySQL datetime format (YYYY-MM-DD HH:MM:SS)
                if (dateString.includes(' ')) {
                    try {
                        // MySQL format: 2025-07-30 19:06:43
                        const [datePart, timePart] = dateString.split(' ');
                        const [year, month, day] = datePart.split('-');
                        const [hour, minute, second] = timePart.split(':');
                        
                        // JavaScript months are 0-indexed (0-11)
                        date = new Date(year, month - 1, day, hour, minute, second);
                        console.log("Parsed MySQL date format:", date);
                    } catch (err) {
                        console.warn("Error parsing MySQL date format:", err);
                        // Fall back to standard parsing
                        date = new Date(dateString);
                    }
                } 
                // Try to parse various ISO formats
                else if (dateString.includes('T')) {
                    // Standard ISO format
                    date = new Date(dateString);
                    console.log("Parsed ISO date format:", date);
                }
                // Try to handle numeric timestamp (seconds or milliseconds)
                else if (/^\d+$/.test(dateString)) {
                    const timestamp = parseInt(dateString);
                    
                    // Check if seconds (Unix timestamp) or milliseconds
                    if (timestamp < 10000000000) { // Less than year 2286 in seconds
                        date = new Date(timestamp * 1000); // Convert seconds to ms
                    } else {
                        date = new Date(timestamp); // Already in ms
                    }
                    console.log("Parsed timestamp:", date);
                }
                // Other string format - try standard parsing
                else {
                    date = new Date(dateString);
                    console.log("Parsed with standard Date constructor:", date);
                }
            } else if (dateString instanceof Date) {
                date = dateString;
                console.log("Already a Date object:", date);
            } else {
                console.warn("Unhandled date type:", typeof dateString);
                return "Fecha no disponible";
            }
            
            if (isNaN(date.getTime())) {
                console.warn("Invalid date (NaN) after parsing:", dateString);
                return "Fecha no disponible";
            }
            
            return new Intl.DateTimeFormat('es-MX', {
                year: 'numeric',
                month: 'long',
                day: 'numeric',
                hour: '2-digit',
                minute: '2-digit'
            }).format(date);
        } catch (e) {
            console.error("Error formatting date:", e, "with input:", dateString);
            return "Fecha no disponible";
        }
    };

    const handleCustomerFieldChange = (field, value) => {
        setEditedCustomer(prev => ({
            ...prev,
            [field]: value
        }));
    };

    const handleSaveCustomerChanges = async () => {
        setIsSubmitting(true);
        
        try {
            // Verify that we have a valid customer ID before trying to update
            if (!editedCustomer.id || editedCustomer.id === 0) {
                console.error('Attempting to update customer with invalid ID:', editedCustomer.id);
                onError('Error: No se puede actualizar el cliente sin un ID válido. Por favor intente nuevamente desde el inicio.');
                return;
            }
            
            console.log(`Actualizando cliente con ID: ${editedCustomer.id}`);
            console.log(`RFC original: ${originalRFC}, RFC nuevo: ${editedCustomer.rfc}`);
            
            // Prepare customer data with both original RFC and new RFC for the update
            const customerDataWithRFC = {
                ...editedCustomer,
                originalRFC: originalRFC // Pass original RFC for API payload
            };
            
            // Call API to update customer data
            const result = await updateCustomer(customerDataWithRFC);
            
            if (!result.success) {
                // Display error similar to PAC errors in invoice generation
                onError(result.error);
                return;
            }
            
            // If successful (200), exit edit mode and continue
            setIsEditing(false);
            
            // Update the edited customer with the result from API
            const updatedCustomer = result.data;
            setEditedCustomer(updatedCustomer);
            
            // Update the original RFC to the new RFC after successful update
            setOriginalRFC(updatedCustomer.rfc);
            
            // Log successful update
            console.log('Cliente actualizado exitosamente:', JSON.stringify(updatedCustomer, null, 2));
            
        } catch (error) {
            console.error('Error updating customer:', error);
            onError('Error al actualizar los datos del cliente. Por favor intente nuevamente.');
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleCancelEdit = () => {
        setEditedCustomer({ ...customer });
        setIsEditing(false);
    };

    const handleConfirmSale = async () => {
        setIsSubmitting(true);
        
        try {
            // Validate that products have IDs before proceeding
            const productsWithoutIds = (safeSale.productos || []).filter(product => !product.id || product.id === 0);
            if (productsWithoutIds.length > 0) {
                console.error('Products without IDs found:', productsWithoutIds);
                onError('Error: Algunos productos no tienen ID válido. Por favor intente nuevamente.');
                setIsSubmitting(false);
                return;
            }

            // Prepare final sale data with updated customer info
            const finalSaleData = {
                ...safeSale,
                // Añadimos el customer actualizado aquí para que se pase correctamente al siguiente paso
                customer: editedCustomer
            };

            console.log('Final sale data being sent to invoice generator:', JSON.stringify(finalSaleData, null, 2));
            console.log('Datos del cliente editado que se enviarán:', JSON.stringify(editedCustomer, null, 2));
            
            onSaleDetailsConfirmed(finalSaleData);
            
        } catch (error) {
            console.error('Error confirming sale:', error);
            onError('Error al confirmar los datos de la venta. Por favor intente nuevamente.');
        } finally {
            setIsSubmitting(false);
        }
    };

    // const calculateSubtotal = () => {
    //     // Handle the new API response structure which uses 'importe' instead of 'subtotal'
    //     return safeSale.productos?.reduce((sum, product) => sum + (product.subtotal || 0), 0) || 0;
    // };

    // const calculateTax = () => {
    //     return calculateSubtotal() * 0.16; // 16% IVA
    // };

    // const calculateTotal = () => {
    //     return calculateSubtotal() + calculateTax();
    // };

    const calculateSubtotal = () => {
        // Handle the new API response structure which uses 'importe' instead of 'subtotal'
        return safeSale.total / 1.16;
    };

    const calculateTax = () => {
        return calculateSubtotal() * 0.16;
    };

    const calculateTotal = () => {
        return safeSale.total;
    };

    return (
        <div className="sale-details">
            <div className="details-header">
                <h2>Detalles de la Venta</h2>
                <p>Revise los detalles de su compra y datos fiscales antes de generar la factura</p>
            </div>

            {/* Sale Information */}
            <div className="sale-info-section">
                <h3>Información de la Venta</h3>
                <div className="sale-summary">
                    <div className="summary-row">
                        <span className="label">Fecha de Venta:</span>
                        <span className="value">{formatDate(safeSale.fechaVenta)}</span>
                    </div>
                    <div className="summary-row">
                        <span className="label">Sucursal:</span>
                        <span className="value">
                            {safeSale.sucursalName || `Sucursal #${safeSale.sucursalId}`}
                        </span>
                    </div>
                    <div className="summary-row">
                        <span className="label">ID de Venta:</span>
                        <span className="value">{safeSale.id}</span>
                    </div>
                </div>
            </div>

            {/* Products Table */}
            <div className="products-section">
                <h3>Productos</h3>
                <div className="products-table">
                    <table>
                        <thead>
                            <tr>
                                <th>Producto</th>
                                <th>Cantidad</th>
                                <th>Precio Unitario</th>
                                <th>Descuento</th>
                                <th>Subtotal</th>
                            </tr>
                        </thead>
                        <tbody>
                            {(safeSale.productos || []).map((product, index) => (
                                <tr key={product.id || index}>
                                    <td>{product.nombre || 'Producto sin nombre'}</td>
                                    <td>{product.cantidad || 0}</td>
                                    <td>{formatCurrency(product.precioUnitario / 1.16 || 0)}</td>
                                    <td>
                                        {product.descuento ? formatCurrency(product.descuento / 1.16) : '-'}
                                    </td>
                                    <td>{formatCurrency(product.subtotal / 1.16 || 0)}</td>
                                </tr>
                            ))}
                            {(!safeSale.productos || safeSale.productos.length === 0) && (
                                <tr>
                                    <td colSpan="5" style={{textAlign: 'center'}}>
                                        No hay productos disponibles
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>

                {/* Totals */}
                <div className="totals-section">
                    <div className="totals-row">
                        <span className="label">Subtotal:</span>
                        <span className="value">{formatCurrency(calculateSubtotal())}</span>
                    </div>
                    <div className="totals-row">
                        <span className="label">IVA (16%):</span>
                        <span className="value">{formatCurrency(calculateTax())}</span>
                    </div>
                    <div className="totals-row total">
                        <span className="label">Total:</span>
                        <span className="value">{formatCurrency(calculateTotal())}</span>
                    </div>
                </div>
            </div>

            {/* Customer Data Section */}
            <div className="customer-data-section">
                <div className="section-header">
                    <h3>Datos Fiscales</h3>
                    <button
                        onClick={() => setIsEditing(!isEditing)}
                        className="btn-secondary"
                        disabled={isSubmitting}
                    >
                        {isEditing ? 'Cancelar Edición' : '✏️ Editar'}
                    </button>
                </div>

                {isEditing ? (
                    <div className="customer-edit-form">
                        {/* RFC */}
                        <div className="input-group">
                            <label>RFC:</label>
                            <input
                                type="text"
                                value={editedCustomer.rfc}
                                onChange={(e) => handleCustomerFieldChange('rfc', e.target.value.toUpperCase())}
                                maxLength="13"
                            />
                        </div>

                        {/* Razón Social */}
                        <div className="input-group">
                            <label>Razón Social:</label>
                            <input
                                type="text"
                                value={editedCustomer.razonSocial}
                                onChange={(e) => handleCustomerFieldChange('razonSocial', e.target.value)}
                            />
                        </div>

                        {/* Email */}
                        <div className="input-group">
                            <label>Email:</label>
                            <input
                                type="email"
                                value={editedCustomer.email || ''}
                                onChange={(e) => handleCustomerFieldChange('email', e.target.value)}
                                placeholder="Opcional"
                            />
                        </div>

                        {/* Telefono */}
                        <div className="input-group">
                            <label>Teléfono:</label>
                            <input
                                type="tel"
                                value={editedCustomer.telefono || ''}
                                onChange={(e) => handleCustomerFieldChange('telefono', e.target.value)}
                                placeholder="Opcional"
                                maxLength="10"
                            />
                        </div>

                        {/* Codigo Postal */}
                        <div className="input-group">
                            <label>Código Postal:</label>
                            <input
                                type="text"
                                value={editedCustomer.codigoPostal}
                                onChange={(e) => handleCustomerFieldChange('codigoPostal', e.target.value)}
                                maxLength="5"
                                placeholder="12345"
                            />
                        </div>

                        {/* Uso CFDI */}
                        <div className="input-group">
                            <label>Uso de CFDI:</label>
                            <select
                                value={editedCustomer.usoCfdi}
                                onChange={(e) => handleCustomerFieldChange('usoCfdi', e.target.value)}
                            >
                                {CFDI_USAGE_OPTIONS.map(option => (
                                    <option key={option.value} value={option.value}>
                                        {option.label}
                                    </option>
                                ))}
                            </select>
                        </div>

                        {/* Regimen Fiscal */}
                        <div className="input-group">
                            <label>Régimen Fiscal:</label>
                            <select
                                value={editedCustomer.regimenFiscal}
                                onChange={(e) => handleCustomerFieldChange('regimenFiscal', e.target.value)}
                            >
                                {FISCAL_REGIME_OPTIONS.map(option => (
                                    <option key={option.value} value={option.value}>
                                        {option.label}
                                    </option>
                                ))}
                            </select>
                        </div>

                        <div className="edit-actions">
                            <button
                                onClick={handleCancelEdit}
                                className="btn-secondary"
                                disabled={isSubmitting}
                            >
                                Cancelar
                            </button>
                            <button
                                onClick={handleSaveCustomerChanges}
                                className="btn-primary"
                                disabled={isSubmitting}
                            >
                                {isSubmitting ? 'Guardando...' : 'Guardar Cambios'}
                            </button>
                        </div>
                    </div>
                ) : (
                    <div className="customer-display">
                        <div className="customer-info">
                            <div className="info-row">
                                <span className="label">RFC:</span>
                                <span className="value">{editedCustomer.rfc}</span>
                            </div>
                            <div className="info-row">
                                <span className="label">Razón Social:</span>
                                <span className="value">{editedCustomer.razonSocial}</span>
                            </div>
                            {editedCustomer.email && (
                                <div className="info-row">
                                    <span className="label">Email:</span>
                                    <span className="value">{editedCustomer.email}</span>
                                </div>
                            )}
                            {editedCustomer.telefono && (
                                <div className="info-row">
                                    <span className="label">Teléfono:</span>
                                    <span className="value">{editedCustomer.telefono}</span>
                                </div>
                            )}
                            <div className="info-row">
                                <span className="label">Código Postal:</span>
                                <span className="value">{editedCustomer.codigoPostal}</span>
                            </div>
                            <div className="info-row">
                                <span className="label">Uso de CFDI:</span>
                                <span className="value">
                                    {getCfdiUsageOption(editedCustomer.usoCfdi)?.label || editedCustomer.usoCfdi}
                                </span>
                            </div>
                            <div className="info-row">
                                <span className="label">Régimen Fiscal:</span>
                                <span className="value">
                                    {getFiscalRegimeOption(editedCustomer.regimenFiscal)?.label || editedCustomer.regimenFiscal}
                                </span>
                            </div>
                        </div>
                    </div>
                )}
            </div>

            {/* Important Notice */}
            <div className="important-notice">
                <h4>⚠️ Importante</h4>
                <p>
                    Verifique que todos los datos sean correctos antes de continuar. 
                    Una vez generada la factura, no podrá modificar esta información.
                </p>
            </div>

            {/* Actions */}
            <div className="form-actions">
                <button
                    onClick={onGoBack}
                    className="btn-secondary"
                    disabled={isSubmitting}
                >
                    ← Regresar
                </button>
                
                <button
                    onClick={handleConfirmSale}
                    className="btn-primary"
                    disabled={isSubmitting || isEditing}
                >
                    {isSubmitting ? 'Procesando...' : 'Generar Factura →'}
                </button>
            </div>
        </div>
    );
};

SaleDetails.propTypes = {
    sale: PropTypes.shape({
        id: PropTypes.number,
        sucursalId: PropTypes.number,
        fechaVenta: PropTypes.string,
        total: PropTypes.number,
        formaPago: PropTypes.string,
        productos: PropTypes.arrayOf(PropTypes.shape({
            id: PropTypes.number,
            nombre: PropTypes.string,
            cantidad: PropTypes.number,
            precioUnitario: PropTypes.number,
            subtotal: PropTypes.number,
            importe: PropTypes.number,
            descuento: PropTypes.number,
            claveUnidad: PropTypes.string,
            claveUnidadDescripcion: PropTypes.string,
            claveProdServ: PropTypes.string,
            productoServicio: PropTypes.string
        }))
    }),
    customer: PropTypes.shape({
        id: PropTypes.number,
        rfc: PropTypes.string,
        razonSocial: PropTypes.string,
        email: PropTypes.string,
        telefono: PropTypes.string,
        codigoPostal: PropTypes.string,
        usoCfdi: PropTypes.string,
        regimenFiscal: PropTypes.string
    }),
    onSaleDetailsConfirmed: PropTypes.func.isRequired,
    onError: PropTypes.func.isRequired,
    onGoBack: PropTypes.func.isRequired
};

// Default props to handle cases when data is missing
SaleDetails.defaultProps = {
    sale: {
        id: 0,
        sucursalId: 0,
        fechaVenta: new Date().toISOString(),
        total: 0,
        productos: []
    },
    customer: {
        id: 0,
        rfc: '',
        razonSocial: '',
        email: '',
        telefono: '',
        codigoPostal: '',
        usoCfdi: 'G03', // Default to "Gastos en general" as most common
        regimenFiscal: '616' // Default to "Sin obligaciones fiscales"
    }
};

export default SaleDetails;
